import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { recipeAPI } from '../services/api';

function RecipeEdit({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    servings: '',
    prep_time: '',
    cook_time: '',
    total_time: '',
    ingredients: [{ quantity: '', item: '' }],
    instructions: ['']
  });

  useEffect(() => {
    loadRecipe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadRecipe = async () => {
    try {
      setLoading(true);
      const response = await recipeAPI.get(parseInt(id));
      const recipeData = response.data.recipe;
      setRecipe(recipeData);
      
      // Parse ingredients - they might be in format "quantity item" or just "item"
      const parsedIngredients = (recipeData.ingredients || []).map(ing => {
        const text = ing.item || '';
        // Try to split quantity and item
        const parts = text.trim().split(/\s+(.+)/);
        if (parts.length > 1 && parts[0].trim()) {
          return { quantity: parts[0].trim(), item: parts[1].trim() };
        }
        return { quantity: '', item: text.trim() };
      });
      
      setFormData({
        title: recipeData.title || '',
        description: recipeData.description || '',
        servings: recipeData.servings || '',
        prep_time: recipeData.prep_time || '',
        cook_time: recipeData.cook_time || '',
        total_time: recipeData.total_time || '',
        ingredients: parsedIngredients.length > 0 ? parsedIngredients : [{ quantity: '', item: '' }],
        instructions: (recipeData.instructions || []).map(inst => inst.instruction || '').length > 0 
          ? (recipeData.instructions || []).map(inst => inst.instruction || '')
          : ['']
      });
      setError(null);
    } catch (err) {
      setError('Failed to load recipe');
      console.error('Error loading recipe:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    setFormData(prev => ({ ...prev, ingredients: newIngredients }));
  };

  const addIngredient = () => {
    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { quantity: '', item: '' }]
    }));
  };

  const removeIngredient = (index) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const handleInstructionChange = (index, value) => {
    const newInstructions = [...formData.instructions];
    newInstructions[index] = value;
    setFormData(prev => ({ ...prev, instructions: newInstructions }));
  };

  const addInstruction = () => {
    setFormData(prev => ({
      ...prev,
      instructions: [...prev.instructions, '']
    }));
  };

  const removeInstruction = (index) => {
    setFormData(prev => ({
      ...prev,
      instructions: prev.instructions.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.title.trim()) {
      setError('Recipe title is required');
      return;
    }

    setSaving(true);

    try {
      // Format ingredients with quantities
      const formattedIngredients = formData.ingredients
        .filter(ing => ing.item.trim())
        .map(ing => {
          const item = ing.item.trim();
          const quantity = ing.quantity.trim();
          return quantity ? `${quantity} ${item}` : item;
        });

      // Filter out empty instructions
      const formattedInstructions = formData.instructions.filter(inst => inst.trim());

      await recipeAPI.update(recipe.id, {
        title: formData.title,
        description: formData.description,
        servings: formData.servings,
        prep_time: formData.prep_time,
        cook_time: formData.cook_time,
        total_time: formData.total_time,
        ingredients: formattedIngredients,
        instructions: formattedInstructions
      });
      
      // Navigate back to recipe detail page
      navigate(`/recipe/${recipe.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update recipe');
      console.error('Error updating recipe:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="loading-spinner" style={{ width: '40px', height: '40px', borderWidth: '4px' }}></div>
      </div>
    );
  }

  if (error && !recipe) {
    return (
      <div className="app-container">
        <header className="app-header">
          <Link to="/" className="header-logo" style={{ textDecoration: 'none' }}>
            <img src="/logo.png" alt="RecipeWorks" className="header-logo-img" onError={(e) => { e.target.style.display = 'none'; }} />
          </Link>
          <div className="header-actions">
            <span className="user-name">{user?.username}</span>
            <button onClick={onLogout} className="btn btn-secondary">Logout</button>
          </div>
        </header>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--error-color)', marginBottom: '1rem' }}>{error}</p>
          <button onClick={() => navigate('/')} className="btn btn-primary">Back to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <header className="app-header">
        <Link to="/" className="header-logo" style={{ textDecoration: 'none' }}>
          <img src="/logo.png" alt="RecipeWorks" className="header-logo-img" onError={(e) => { e.target.style.display = 'none'; }} />
          <span className="header-logo-text">RecipeWorks</span>
        </Link>
        <div className="header-actions">
          <span className="user-name">{user?.username}</span>
          <button onClick={onLogout} className="btn btn-secondary">Logout</button>
        </div>
      </header>

      <div style={{ maxWidth: '800px', margin: '2rem auto', padding: '0 1rem' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <button onClick={() => navigate(`/recipe/${id}`)} className="btn btn-secondary">
            ← Back to Recipe
          </button>
        </div>

        <div className="recipe-detail-card" style={{ padding: '2rem' }}>
          <h1 style={{ marginBottom: '1.5rem' }}>Edit Recipe</h1>

          {error && (
            <div className="error-message" style={{ marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Recipe Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="e.g., Chocolate Chip Cookies"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Brief description of the recipe"
                rows="3"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>Servings</label>
                <input
                  type="text"
                  name="servings"
                  value={formData.servings}
                  onChange={handleChange}
                  placeholder="e.g., 4"
                />
              </div>
              <div className="form-group">
                <label>Prep Time</label>
                <input
                  type="text"
                  name="prep_time"
                  value={formData.prep_time}
                  onChange={handleChange}
                  placeholder="e.g., 15 min"
                />
              </div>
              <div className="form-group">
                <label>Cook Time</label>
                <input
                  type="text"
                  name="cook_time"
                  value={formData.cook_time}
                  onChange={handleChange}
                  placeholder="e.g., 30 min"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Total Time</label>
              <input
                type="text"
                name="total_time"
                value={formData.total_time}
                onChange={handleChange}
                placeholder="e.g., 45 min"
              />
            </div>

            <div className="modal-section" style={{ marginTop: '2rem' }}>
              <h3>Ingredients</h3>
              {formData.ingredients.map((ingredient, index) => (
                <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={ingredient.quantity}
                    onChange={(e) => handleIngredientChange(index, 'quantity', e.target.value)}
                    placeholder="Quantity (e.g., 2 cups)"
                    style={{ width: '150px', flexShrink: 0 }}
                  />
                  <input
                    type="text"
                    value={ingredient.item}
                    onChange={(e) => handleIngredientChange(index, 'item', e.target.value)}
                    placeholder="Ingredient name"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => removeIngredient(index)}
                    style={{ padding: '0.5rem 1rem' }}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button type="button" className="btn btn-secondary" onClick={addIngredient}>
                + Add Ingredient
              </button>
            </div>

            <div className="modal-section" style={{ marginTop: '2rem' }}>
              <h3>Instructions</h3>
              {formData.instructions.map((instruction, index) => (
                <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ 
                    background: 'var(--primary-blue)', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: '28px', 
                    height: '28px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontWeight: 'bold'
                  }}>
                    {index + 1}
                  </span>
                  <textarea
                    value={instruction}
                    onChange={(e) => handleInstructionChange(index, e.target.value)}
                    placeholder={`Step ${index + 1}`}
                    style={{ flex: 1, minHeight: '60px' }}
                  />
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => removeInstruction(index)}
                    style={{ padding: '0.5rem 1rem' }}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button type="button" className="btn btn-secondary" onClick={addInstruction}>
                + Add Step
              </button>
            </div>

            <div className="modal-actions" style={{ marginTop: '2rem' }}>
              <button type="button" className="btn btn-secondary" onClick={() => navigate(`/recipe/${id}`)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-success" disabled={saving}>
                {saving ? <span className="loading-spinner"></span> : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default RecipeEdit;
